import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Plus, Edit3, Trash2, Car, Tag, Calendar, Ticket, Power,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KINDS = [
  { id: "carpool_free", label: "Trajet co-voiturage offert", icon: Car, color: "bg-mint-100 text-mint-700" },
  { id: "merchant_code", label: "Code remise chez un commerçant", icon: Tag, color: "bg-sun-100 text-sun-700" },
  { id: "event_discount", label: "Réduction sur un événement", icon: Calendar, color: "bg-plum-100 text-plum-700" },
  { id: "event_free", label: "Place gratuite sur un événement", icon: Ticket, color: "bg-pink-100 text-pink-600" },
];

const GROUPS = [
  { id: "senior", label: "Sénior (65+)" },
  { id: "mineur", label: "Mineur (-18)" },
  { id: "jeune_adulte", label: "Jeune adulte (18-25)" },
  { id: "etudiant", label: "Étudiant" },
  { id: "famille_nombreuse", label: "Famille nombreuse" },
  { id: "pmr", label: "PMR" },
  { id: "demandeur_emploi", label: "Demandeur d'emploi" },
];

const EMPTY_FORM = {
  title: "", description: "", kind: "carpool_free", target_groups: [],
  monthly_quota: 4, value_percent: "", code_template: "", icon: "", active: true,
};

export default function AdminPerks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/perks").then((r) => setItems(r.data || [])).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      title: p.title || "",
      description: p.description || "",
      kind: p.kind,
      target_groups: p.target_groups || [],
      monthly_quota: p.monthly_quota || 4,
      value_percent: p.value_percent || "",
      code_template: p.code_template || "",
      icon: p.icon || "",
      active: p.active !== false,
    });
    setEditing(p.id);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Titre requis");
    if (form.target_groups.length === 0) return toast.error("Au moins un groupe cible");
    setBusy(true);
    try {
      const body = {
        ...form,
        value_percent: form.value_percent === "" ? null : Number(form.value_percent),
        monthly_quota: Number(form.monthly_quota) || 4,
      };
      if (editing === "new") await api.post("/admin/perks", body);
      else await api.patch(`/admin/perks/${editing}`, body);
      toast.success(editing === "new" ? "Avantage créé" : "Avantage mis à jour");
      setEditing(null); setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer définitivement "${p.title}" ?`)) return;
    try {
      await api.delete(`/admin/perks/${p.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/perks/${p.id}`, { active: !p.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleGroup = (g) => {
    setForm((f) => ({
      ...f,
      target_groups: f.target_groups.includes(g)
        ? f.target_groups.filter((x) => x !== g)
        : [...f.target_groups, g],
    }));
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="phenix-eyebrow mb-2 inline-flex items-center gap-1.5"><Sparkles size={11} /> Avantages Citoyens</div>
          <h1 className="heading-display text-[1.8rem] sm:text-[2.2rem] text-plum-900">Avantages mairie</h1>
          <p className="text-[13px] text-plum-700/65 mt-1">Créez les avantages auxquels chaque groupe citoyen aura accès.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 px-5 font-bold inline-flex items-center gap-1.5" data-testid="add-perk-btn">
          <Plus size={14} /> Nouvel avantage
        </Button>
      </div>

      {loading ? (
        <div className="phenix-card p-10 animate-pulse h-32" />
      ) : items.length === 0 ? (
        <div className="phenix-card p-10 text-center bg-cream-50/40">
          <Sparkles size={28} className="mx-auto text-plum-700/30 mb-2" />
          <h3 className="font-display font-bold text-plum-900 mb-1">Aucun avantage configuré</h3>
          <p className="text-[13px] text-plum-700/65">Créez votre premier avantage pour offrir des bénéfices aux Istréens éligibles.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="perks-list">
          {items.map((p) => {
            const kind = KINDS.find((k) => k.id === p.kind);
            const Icon = kind?.icon || Sparkles;
            return (
              <article key={p.id} className={`phenix-card p-4 bg-white ${!p.active ? "opacity-60" : ""}`} data-testid={`perk-card-${p.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-plum-100 text-plum-700 grid place-items-center text-2xl">
                    {p.icon || <Icon size={20} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-extrabold text-plum-900 text-[15px] line-clamp-1">{p.title}</h3>
                    <span className={`phenix-pill ${kind?.color || ""} text-[10px]`}>{kind?.label || p.kind}</span>
                  </div>
                  <button onClick={() => toggleActive(p)} title={p.active ? "Désactiver" : "Activer"} className={`shrink-0 p-2 rounded-lg ${p.active ? "text-mint-600" : "text-plum-700/40"} hover:bg-cream-50`} data-testid={`toggle-${p.id}`}>
                    <Power size={15} />
                  </button>
                </div>
                <p className="text-[12px] text-plum-700/75 line-clamp-2 mb-2">{p.description || "—"}</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {p.target_groups.map((g) => (
                    <span key={g} className="phenix-pill bg-plum-100 text-plum-700 text-[10px]">
                      {GROUPS.find((x) => x.id === g)?.label || g}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px] text-plum-700/60 border-t border-cream-100 pt-2">
                  <span>Quota : <strong className="text-plum-900">{p.monthly_quota}/mois</strong></span>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} className="px-2 py-1 rounded hover:bg-cream-50 text-plum-700 inline-flex items-center gap-1" data-testid={`edit-${p.id}`}>
                      <Edit3 size={11} /> Éditer
                    </button>
                    <button onClick={() => remove(p)} className="px-2 py-1 rounded hover:bg-pink-50 text-pink-500 inline-flex items-center gap-1" data-testid={`delete-${p.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && (setEditing(null), setForm(EMPTY_FORM))}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="perk-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nouvel avantage" : "Éditer l'avantage"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Type d&apos;avantage</Label>
              <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="perk-kind" className="rounded-xl h-11 bg-cream-50"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Trajet offert aux séniors" className="rounded-xl h-11 bg-cream-50" data-testid="perk-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl bg-cream-50" data-testid="perk-desc" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Groupes cibles *</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {GROUPS.map((g) => (
                  <label key={g.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-[12px] ${form.target_groups.includes(g.id) ? "bg-plum-50 border-plum-300" : "bg-white border-cream-200"}`}>
                    <input type="checkbox" checked={form.target_groups.includes(g.id)} onChange={() => toggleGroup(g.id)} className="rounded" data-testid={`perk-group-${g.id}`} />
                    <span className="text-plum-900 font-semibold">{g.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Quota/mois</Label>
                <Input type="number" min="1" max="50" value={form.monthly_quota} onChange={(e) => setForm({ ...form, monthly_quota: e.target.value })} className="rounded-xl h-11 bg-cream-50" data-testid="perk-quota" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">% réduction</Label>
                <Input type="number" min="0" max="100" value={form.value_percent} onChange={(e) => setForm({ ...form, value_percent: e.target.value })} placeholder="15" className="rounded-xl h-11 bg-cream-50" data-testid="perk-percent" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Icône (emoji)</Label>
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="🎁" maxLength={4} className="rounded-xl h-11 bg-cream-50 text-center" data-testid="perk-icon" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Préfixe code (optionnel)</Label>
              <Input value={form.code_template} onChange={(e) => setForm({ ...form, code_template: e.target.value })} placeholder="MAIRIE15" maxLength={20} className="rounded-xl h-11 bg-cream-50 font-mono" data-testid="perk-template" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-plum-700 hover:bg-plum-800 text-white h-12 font-bold" data-testid="perk-submit">
              {busy ? "…" : editing === "new" ? "Créer l'avantage" : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
